import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FolderOpen, Upload } from 'lucide-react'
import { apiClient } from '@/services/apiClient'
import { uploadMarketingPhoto } from '@/services/marketing.service'
import './ftp-editor.css'

export interface FtpPhotoPickerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (url: string, alt: string) => void
  /**
   * Show every image cropped to the shape it will be used at (a CSS
   * aspect-ratio, e.g. '390 / 176'), so it is judged as it will look. Absent =
   * square tiles, whole images in the upload preview.
   */
  crop?: { aspectRatio: string; hint: string }
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024

interface Photo {
  id: number
  url: string
  originalFilename: string
  altText: string | null
  folder: string | null
  /** True when the image comes from the master account rather than this one. */
  shared?: boolean
  searchTerms: string
}

interface FolderInfo {
  id?: number
  folder: string
  count: number
  scope?: 'master' | 'account'
  isSystem?: boolean
}

export function FtpPhotoPicker({
  isOpen,
  onClose,
  onSelect,
  crop,
}: FtpPhotoPickerProps) {
  const [activeTab, setActiveTab] = useState<'library' | 'upload'>('library')
  const [search, setSearch] = useState('')
  const [currentFolder, setCurrentFolder] = useState('')
  /** all | mine (this account's own) | shared (from the master account). */
  const [scope, setScope] = useState<'all' | 'mine' | 'shared'>('all')
  const [allPhotos, setAllPhotos] = useState<Photo[]>([])
  const [folders, setFolders] = useState<string[]>([])
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [altText, setAltText] = useState('')
  const [uploadFolder, setUploadFolder] = useState('general')
  // Off by default: an image dropped into one email is usually a one-off, and
  // saving every one of them is what filled the library with campaign clutter.
  // Unchecked, the file goes to S3 and is used on this campaign only.
  const [saveToLibrary, setSaveToLibrary] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState('')
  const [dropHighlight, setDropHighlight] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)

  const resetUploadUi = useCallback(() => {
    setPendingFile(null)
    setPreviewUrl('')
    setAltText('')
    setUploadFolder('general')
    setSaveToLibrary(false)
    setUploadProgress(0)
    setUploading(false)
    setError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const resetLibraryUi = useCallback(() => {
    setSearch('')
    setCurrentFolder('')
    setScope('all')
    setSelectedImage(null)
    setAllPhotos([])
    setFolders([])
    setLoading(false)
    setError('')
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetLibraryUi()
      resetUploadUi()
      setActiveTab('library')
      dragDepthRef.current = 0
      setDropHighlight(false)
    }
  }, [isOpen, resetLibraryUi, resetUploadUi])

  const fetchData = useCallback(async () => {
    if (!isOpen) return
    setLoading(true)
    setError('')
    try {
      // No all_accounts flag: the API already returns this account's own photos
      // plus the ones the master account shares, which is exactly the visible set.
      const [photosRes, foldersRes] = await Promise.all([
        apiClient.get('/marketing/photos'),
        apiClient.get('/marketing/photos/folders'),
      ])
      setAllPhotos(photosRes.data?.data ?? [])
      const folderData: FolderInfo[] = foldersRes.data?.data ?? []
      setFolders(folderData.map((f) => f.folder).filter(Boolean))
    } catch {
      setAllPhotos([])
      setFolders([])
      setError('Could not load images.')
    } finally {
      setLoading(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) void fetchData()
  }, [isOpen, fetchData])

  const q = search.trim().toLowerCase()
  const images = useMemo(() => {
    let result = allPhotos
    if (currentFolder) result = result.filter((p) => p.folder === currentFolder)
    // The list is this account's own photos plus the ones the master account
    // shares, so `shared` alone separates the two — no account id needed.
    if (scope === 'mine') result = result.filter((p) => !p.shared)
    else if (scope === 'shared') result = result.filter((p) => p.shared)
    if (q) result = result.filter((p) => {
      const haystack = `${p.originalFilename ?? ''} ${p.altText ?? ''} ${p.folder ?? ''} ${p.searchTerms ?? ''}`.toLowerCase()
      return haystack.includes(q)
    })
    return result.map((p) => ({
      id: String(p.id),
      url: p.url,
      alt: p.altText || p.originalFilename || '',
    }))
  }, [allPhotos, currentFolder, scope, q])

  const pickFile = (file: File | null | undefined) => {
    setError('')
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.')
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Image must be 10MB or smaller.')
      return
    }
    setPendingFile(file)
    setAltText((file.name.replace(/\.[^.]+$/, '') || file.name).trim())
    const reader = new FileReader()
    reader.onload = () => {
      const r = reader.result
      if (typeof r === 'string') setPreviewUrl(r)
    }
    reader.readAsDataURL(file)
  }

  const performUpload = async () => {
    if (!pendingFile) return
    setUploading(true)
    setError('')
    setUploadProgress(30)
    try {
      const photo = await uploadMarketingPhoto(pendingFile, {
        altText,
        folder: uploadFolder,
        saveToLibrary,
        onProgress: (pct) => setUploadProgress(pct),
      })
      const url = photo?.url
      if (!url) {
        setError('Upload succeeded but no image URL was returned.')
        setUploadProgress(0)
        setUploading(false)
        return
      }
      const alt = altText
      setUploadProgress(100)
      // Only a library save changes what the Library tab would show.
      if (saveToLibrary) void fetchData()
      onSelect(url, alt)
      window.setTimeout(() => onClose(), 500)
    } catch {
      setError('Upload failed.')
      setUploadProgress(0)
    } finally {
      setUploading(false)
    }
  }

  const insertFromLibrary = () => {
    if (!selectedImage) return
    onSelect(selectedImage.url, selectedImage.alt)
    onClose()
  }

  if (!isOpen) return null

  const folderOptions = Array.from(new Set(['general', ...folders]))

  const footerInfo =
    activeTab === 'library'
      ? selectedImage
        ? selectedImage.alt || selectedImage.url
        : 'Select an image from the library.'
      : pendingFile
        ? `${pendingFile.name} · ${(pendingFile.size / 1024).toFixed(0)} KB`
        : 'Drop an image or choose a file to upload.'

  const dropzoneStyle =
    dropHighlight
      ? { borderColor: '#89b4fa', background: 'rgba(137,180,250,0.08)' as const }
      : undefined

  return (
    <div
      className={`ftp-modal-overlay active`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className="ftp-modal ftp-photo-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ftp-photo-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ftp-modal-header">
          <h3 id="ftp-photo-picker-title">Insert image</h3>
          <button type="button" className="ftp-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="ftp-picker-tabs">
          <button
            type="button"
            className={`ftp-picker-tab${activeTab === 'library' ? ' active' : ''}`}
            onClick={() => {
              setActiveTab('library')
              setError('')
            }}
          >
            <FolderOpen className="ftp-picker-tab-icon" aria-hidden />
            Library
          </button>
          <button
            type="button"
            className={`ftp-picker-tab${activeTab === 'upload' ? ' active' : ''}`}
            onClick={() => {
              setActiveTab('upload')
              setError('')
            }}
          >
            <Upload className="ftp-picker-tab-icon" aria-hidden />
            Upload
          </button>
        </div>

        <div className="ftp-picker-body">
          <div className={`ftp-picker-panel${activeTab === 'library' ? ' active' : ''}`}>
            {/* Search and both filters on one line, held above the grid while it
                scrolls — dropdowns rather than a row of folder chips, which
                wrapped over several lines once accounts ran dozens of folders. */}
            <div className="ftp-library-toolbar">
              <div className="ftp-library-search">
                <i aria-hidden>⌕</i>
                <input
                  type="search"
                  placeholder="Search photos..."
                  autoComplete="off"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="ftp-library-filter"
                aria-label="Filter by folder"
                value={currentFolder}
                onChange={(e) => {
                  setCurrentFolder(e.target.value)
                  setSelectedImage(null)
                }}
              >
                <option value="">All folders</option>
                {folders.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <select
                className="ftp-library-filter"
                aria-label="Filter by owner"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as 'all' | 'mine' | 'shared')
                  setSelectedImage(null)
                }}
              >
                <option value="all">All photos</option>
                <option value="mine">This account</option>
                <option value="shared">Shared</option>
              </select>
            </div>

            {error && activeTab === 'library' && (
              <p className="ftp-picker-info" style={{ color: '#f38ba8', marginBottom: 8 }}>
                {error}
              </p>
            )}
            {crop && (
              <p className="ftp-picker-info" style={{ marginBottom: 8 }}>
                {crop.hint}
              </p>
            )}

            <div className="ftp-picker-scroll">
            <div
              className="ftp-picker-grid"
              // Wider tiles for a wide crop, so each reads as the band it becomes.
              style={crop ? { gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' } : undefined}
            >
              {loading ? (
                <div className="ftp-picker-loading">Loading…</div>
              ) : images.length === 0 ? (
                <div
                  className="ftp-picker-info"
                  style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '30px' }}
                >
                  No images found.
                </div>
              ) : (
                images.map((img) => (
                  <div
                    key={img.id}
                    className={`ftp-picker-img-item${selectedImage?.url === img.url ? ' selected' : ''}`}
                    style={crop ? { aspectRatio: crop.aspectRatio } : undefined}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedImage({ url: img.url, alt: img.alt })}
                    onDoubleClick={() => {
                      // Double-click = select + insert (same as the Insert button).
                      onSelect(img.url, img.alt)
                      onClose()
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedImage({ url: img.url, alt: img.alt })
                      }
                    }}
                  >
                    <img src={img.url} alt={img.alt} loading="lazy" decoding="async" />
                    <span
                      className="ftp-picker-info"
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        right: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                      }}
                    >
                      {img.alt || img.url}
                    </span>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>

          <div className={`ftp-picker-panel${activeTab === 'upload' ? ' active' : ''}`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {/* The file comes first: nothing but the dropzone until one is
                chosen, then the preview on the left with its options beside it.
                Asking for alt text and a folder before there is an image to
                describe put the questions in the wrong order. */}
            {!pendingFile ? (
              <div
                className="ftp-upload-dropzone ftp-upload-dropzone-empty"
                style={dropzoneStyle}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault()
                  dragDepthRef.current += 1
                  setDropHighlight(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  dragDepthRef.current -= 1
                  if (dragDepthRef.current <= 0) {
                    dragDepthRef.current = 0
                    setDropHighlight(false)
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  dragDepthRef.current = 0
                  setDropHighlight(false)
                  pickFile(e.dataTransfer.files?.[0])
                }}
              >
                <Upload className="ftp-upload-dropzone-icon" aria-hidden />
                <div>Drag &amp; drop an image here, or click to browse</div>
                <div className="ftp-upload-hint">PNG, JPG, GIF, WebP · Max file size: 10 MB</div>
              </div>
            ) : (
              <div className="ftp-upload-layout">
                <div className="ftp-upload-preview-col">
                  <div
                    className="ftp-upload-preview-box"
                    style={crop ? { aspectRatio: crop.aspectRatio, height: 'auto' } : undefined}
                  >
                    {previewUrl && <img src={previewUrl} alt="" style={crop ? { objectFit: 'cover' } : undefined} />}
                    <button
                      type="button"
                      className="ftp-upload-preview-remove"
                      aria-label="Remove image"
                      onClick={resetUploadUi}
                    >
                      ×
                    </button>
                  </div>
                  <p className="ftp-upload-filename" title={pendingFile.name}>
                    {pendingFile.name}
                  </p>
                  <p className="ftp-upload-hint">{(pendingFile.size / 1024).toFixed(0)} KB</p>
                </div>

                <div className="ftp-upload-form">
                  <div className="ftp-field-group">
                    <label htmlFor="ftp-upload-alt">Alt text</label>
                    <input
                      id="ftp-upload-alt"
                      type="text"
                      value={altText}
                      onChange={(e) => setAltText(e.target.value)}
                    />
                  </div>

                  <div className="ftp-field-group">
                    <label htmlFor="ftp-upload-folder">Folder</label>
                    <select
                      id="ftp-upload-folder"
                      value={uploadFolder}
                      disabled={!saveToLibrary}
                      onChange={(e) => setUploadFolder(e.target.value)}
                    >
                      {folderOptions.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="ftp-upload-toggle" htmlFor="ftp-upload-save-library">
                    <input
                      id="ftp-upload-save-library"
                      type="checkbox"
                      checked={saveToLibrary}
                      onChange={(e) => setSaveToLibrary(e.target.checked)}
                    />
                    <span>
                      Add to Photo Library
                      <span className="ftp-upload-hint">
                        {saveToLibrary
                          ? 'Saved to this account only — other accounts will not see it.'
                          : 'Off: the image is uploaded and used here only, not kept in the library.'}
                      </span>
                    </span>
                  </label>

                  {error && activeTab === 'upload' && (
                    <p className="ftp-picker-info" style={{ color: '#f38ba8' }}>{error}</p>
                  )}

                  {uploading && (
                    <div className="ftp-upload-progress">
                      <div className="ftp-progress-bar" aria-hidden>
                        <div className="ftp-progress-fill" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <span className="ftp-picker-info">{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && activeTab === 'upload' && !pendingFile && (
              <p className="ftp-picker-info" style={{ color: '#f38ba8', marginTop: 8 }}>
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="ftp-modal-footer">
          <span className="ftp-picker-info">{footerInfo}</span>
          <div className="ftp-modal-actions">
            <button type="button" className="ftp-btn ftp-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            {activeTab === 'library' ? (
              <button
                type="button"
                className="ftp-btn ftp-btn-primary"
                disabled={!selectedImage}
                onClick={insertFromLibrary}
              >
                Insert Image
              </button>
            ) : (
              <button
                type="button"
                className="ftp-btn ftp-btn-upload"
                disabled={!pendingFile || uploading}
                onClick={() => void performUpload()}
              >
                Upload &amp; Insert
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
